import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown, ArrowUpRight, Check, ClipboardCheck, FileCheck2,
  FolderKanban, LockKeyhole, Network, ShieldCheck,
} from "lucide-react";
import { NRSLogo, NRSWordmark } from "../components/NRSLogo";
import { LangToggle, useT } from "../lib/i18n";
import heroImage from "../assets/nrs-chip-hero.png";
import "./Home.css";

const COOKIE_NOTICE_KEY = "nrs_cookie_notice_dismissed_v1";

function SectionKicker({ number, children }) {
  return <div className="section-kicker"><span>{number}</span><i />{children}</div>;
}

function CookiePolicyBanner({ open, onDismiss, t }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  if (!open) return null;

  return (
    <aside className="home-cookie-banner" aria-label={t("home.cookies.aria")} data-testid="cookie-policy-banner">
      <div className="home-cookie-content">
        <div className="home-cookie-copy">
          <h2>{t("home.cookies.title")}</h2>
          <p>{t("home.cookies.summary")}</p>
          <p className="home-cookie-details" id="home-cookie-details" hidden={!detailsOpen}>{t("home.cookies.technical")}</p>
        </div>
        <div className="home-cookie-actions">
          <button
            type="button"
            className="home-cookie-detail-button"
            aria-expanded={detailsOpen}
            aria-controls="home-cookie-details"
            onClick={() => setDetailsOpen((openDetails) => !openDetails)}
          >
            {t(detailsOpen ? "home.cookies.hideDetails" : "home.cookies.details")}
          </button>
          <button type="button" className="home-cookie-dismiss" onClick={onDismiss}>
            {t("home.cookies.close")}
          </button>
        </div>
      </div>
    </aside>
  );
}

function PreviewWindow({ kind, title, index, t }) {
  return (
    <article className={`preview-card preview-${kind}`}>
      <div className="preview-topline">
        <span className="preview-index">{index}</span>
        <span className="preview-window-dots"><i /><i /><i /></span>
        <span className="preview-window-title">{t(title)}</span>
      </div>
      {kind === "roles" && (
        <div className="mock-roles">
          <div className="mock-role-selected">
            <span>{t("home.preview.roles.current")}</span>
            <b>{t("home.preview.roles.manufacturer")}</b>
            <small>{t("home.preview.roles.note")}</small>
          </div>
          <div className="mock-role-options">
            <div className="mock-role-option selected">
              <span>01</span><div><b>{t("home.preview.roles.manufacturer")}</b><small>{t("home.preview.roles.manufacturer_detail")}</small></div><Check size={13} />
            </div>
            <div className="mock-role-option">
              <span>02</span><div><b>{t("home.preview.roles.importer")}</b><small>{t("home.preview.roles.importer_detail")}</small></div><i />
            </div>
            <div className="mock-role-option">
              <span>03</span><div><b>{t("home.preview.roles.distributor")}</b><small>{t("home.preview.roles.distributor_detail")}</small></div><i />
            </div>
          </div>
          <div className="mock-role-foot"><ShieldCheck size={13} />{t("home.preview.roles.legal_note")}</div>
        </div>
      )}
      {kind === "udi" && (
        <div className="mock-udi">
          <div className="mock-udi-heading">
            <span>{t("home.preview.udi.record")}</span>
            <b>{t("home.preview.udi.prepared")}</b>
          </div>
          <div className="mock-udi-field"><span>{t("home.preview.udi.basic")}</span><b>{t("home.preview.udi.example_basic")}</b></div>
          <div className="mock-udi-field"><span>{t("home.preview.udi.device")}</span><b>{t("home.preview.udi.example_device")}</b></div>
          <div className="mock-udi-field"><span>{t("home.preview.udi.srn")}</span><b>{t("home.preview.udi.example_srn")}</b></div>
          <div className="mock-udi-foot"><FileCheck2 size={13} />{t("home.preview.udi.note")}</div>
        </div>
      )}
      {kind === "pms" && (
        <div className="mock-pms">
          <div className="mock-pms-heading">
            <div><span>{t("home.preview.pms.title")}</span><small>{t("home.preview.pms.subtitle")}</small></div>
            <span className="mock-pms-period">{t("home.preview.pms.period")}</span>
          </div>
          <div className="mock-pms-row"><span className="mock-pms-dot" /><div><b>{t("home.preview.pms.batch")}</b><small>{t("home.preview.pms.batch_detail")}</small></div><em>01</em></div>
          <div className="mock-pms-row"><span className="mock-pms-dot amber" /><div><b>{t("home.preview.pms.feedback")}</b><small>{t("home.preview.pms.feedback_detail")}</small></div><em>02</em></div>
          <div className="mock-pms-row"><span className="mock-pms-dot teal" /><div><b>{t("home.preview.pms.review")}</b><small>{t("home.preview.pms.review_detail")}</small></div><em>03</em></div>
          <div className="mock-pms-foot"><Network size={13} />{t("home.preview.pms.note")}</div>
        </div>
      )}
      <div className="preview-caption"><span>{t(title)}</span><span>{t(`home.preview.caption.${kind}`)}</span></div>
    </article>
  );
}

export default function Home() {
  const t = useT();
  const [cookieOpen, setCookieOpen] = useState(() => {
    try {
      return window.localStorage.getItem(COOKIE_NOTICE_KEY) !== "acknowledged";
    } catch {
      return true;
    }
  });

  function dismissCookieNotice() {
    try {
      window.localStorage.setItem(COOKIE_NOTICE_KEY, "acknowledged");
    } catch {
      // The notice can still be dismissed for this visit if storage is unavailable.
    }
    setCookieOpen(false);
  }

  return (
    <>
    <main className="home-shell">
      <div className="home-grain" aria-hidden="true" />
      <header className="home-header">
        <Link className="home-brand" to="/" aria-label="MDRFlow — European Medical Device Regulatory Platform">
          <NRSLogo size={36} />
          <span className="home-wordmark"><NRSWordmark /></span>
        </Link>
        <nav className="home-nav" aria-label={t("home.nav.account")}>
          <a className="home-nav-anchor" href="#pricing">{t("home.nav.pricing")}</a>
          <LangToggle className="home-language" />
          <Link to="/login" className="home-nav-login">{t("login.submit")}</Link>
        </nav>
      </header>

      <section className="home-hero">
        <div className="home-visual" aria-hidden="true">
          <img className="home-image" src={heroImage} alt="" />
          <div className="home-image-shade" />
          <div className="home-orbit home-orbit-one" />
          <div className="home-orbit home-orbit-two" />
          <div className="home-visual-index">MDRFlow / 01—03</div>
          <div className="home-workspace-card">
            <div className="home-card-head">
              <span className="home-card-label">{t("home.panel.label")}</span>
              <span className="home-live"><i />{t("home.panel.status")}</span>
            </div>
            <div className="home-card-rule" />
            <div className="home-card-row"><span className="home-row-mark">01</span><span>{t("home.panel.mdr")}</span><Check size={14} /></div>
            <div className="home-card-row"><span className="home-row-mark">02</span><span>{t("home.panel.qms")}</span><Check size={14} /></div>
            <div className="home-card-row"><span className="home-row-mark">03</span><span>{t("home.panel.risk")}</span><Check size={14} /></div>
            <div className="home-card-rule home-card-rule-bottom" />
            <div className="home-trace"><ShieldCheck size={15} /><span>{t("home.panel.trace")}</span></div>
          </div>
          <div className="home-coordinate">51°30&apos;26.4&quot;N&nbsp; 00°07&apos;39.0&quot;W</div>
        </div>
        <div className="home-copy">
          <div className="home-eyebrow"><span className="home-eyebrow-line" />{t("home.eyebrow")}</div>
          <h1>{t("home.title")}</h1>
          <p className="home-description">{t("home.description")}</p>
          <p className="home-motto">{t("home.motto")}</p>
          <div className="home-actions">
            <Link to="/login" className="home-primary"><span>{t("home.login")}</span><ArrowUpRight size={17} /></Link>
            <Link to="/register" className="home-secondary"><span>{t("home.register")}</span><span className="home-secondary-arrow">→</span></Link>
          </div>
          <div className="home-assurance"><LockKeyhole size={14} /><span>{t("home.note")}</span></div>
        </div>
        <div className="home-side-rail" aria-hidden="true"><span>REGULATORY OPERATIONS</span><b>01</b></div>
      </section>

      <div className="home-footer">
        <div className="home-footer-proof"><span className="home-proof-icon"><ShieldCheck size={16} /></span>{t("home.secure")}</div>
        <div className="home-footer-standards"><span>{t("home.footer")}</span><i /></div>
        <div className="home-footer-copyright">© 2026 MDRFlow</div>
      </div>

      <section className="section-wrap py-8" aria-labelledby="eudamed-mandatory-title" data-testid="eudamed-mandatory-notice">
        <div className="rounded-sm border border-cyan-800/70 bg-[#0B1725] p-5 sm:p-7">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300">{t("home.eudamed.eyebrow")}</div>
          <h2 id="eudamed-mandatory-title" className="mt-2 text-xl sm:text-2xl font-bold text-white">{t("home.eudamed.title")}</h2>
          <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-300">{t("home.eudamed.body")}</p>
          <p className="mt-3 max-w-4xl text-xs leading-relaxed text-slate-400">{t("home.eudamed.note")}</p>
          <a
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-cyan-300 hover:text-white"
            href="https://health.ec.europa.eu/medical-devices-eudamed/overview_en"
            target="_blank"
            rel="noreferrer"
          >
            {t("home.eudamed.source")} <ArrowUpRight size={13} />
          </a>
        </div>
      </section>

      <section className="home-intro section-wrap">
        <SectionKicker number="01">{t("home.intro.kicker")}</SectionKicker>
        <div className="intro-layout">
          <h2>{t("home.intro.title")}</h2>
          <div className="intro-copy"><p>{t("home.intro.body")}</p><a href="#preview">{t("home.intro.link")}<ArrowDown size={14} /></a></div>
        </div>
        <div className="intro-signals">
          <div><span className="signal-icon"><ClipboardCheck size={19} /></span><b>{t("home.intro.signal1.title")}</b><p>{t("home.intro.signal1.body")}</p></div>
          <div><span className="signal-icon"><FolderKanban size={19} /></span><b>{t("home.intro.signal2.title")}</b><p>{t("home.intro.signal2.body")}</p></div>
          <div><span className="signal-icon"><ShieldCheck size={19} /></span><b>{t("home.intro.signal3.title")}</b><p>{t("home.intro.signal3.body")}</p></div>
        </div>
      </section>

      <section className="home-gallery section-wrap" id="preview">
        <div className="gallery-heading">
          <div><SectionKicker number="02">{t("home.gallery.kicker")}</SectionKicker><h2>{t("home.gallery.title")}</h2></div>
          <p>{t("home.gallery.description")}</p>
        </div>
        <div className="preview-grid">
          <PreviewWindow kind="roles" title="home.preview.roles.name" index="01" t={t} />
          <PreviewWindow kind="udi" title="home.preview.udi.name" index="02" t={t} />
          <PreviewWindow kind="pms" title="home.preview.pms.name" index="03" t={t} />
        </div>
        <div className="gallery-note"><span className="gallery-note-mark">i</span>{t("home.gallery.note")}</div>
      </section>

      <section className="home-quote-section">
        <div className="quote-inner section-wrap">
          <div className="quote-intro"><SectionKicker number="03">{t("home.quotes.kicker")}</SectionKicker><h2>{t("home.quotes.title")}</h2><p>{t("home.quotes.disclaimer")}</p></div>
          <div className="quote-grid">
            {[1, 2, 3, 4].map((item) => (
              <blockquote className="quote-card" key={item}>
                <div className="quote-mark">“</div>
                <p>{t(`home.quotes.${item}.text`)}</p>
                  <footer><span className="quote-avatar">{t(`home.quotes.${item}.initials`)}</span><span><b>{t(`home.quotes.${item}.name`)}</b><small>{t(`home.quotes.${item}.role`)}</small></span><i>{t("home.quotes.label")}</i></footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="home-pricing section-wrap" id="pricing">
        <div className="pricing-heading">
          <div><SectionKicker number="04">{t("home.pricing.kicker")}</SectionKicker><h2>{t("home.pricing.title")}</h2></div>
          <p>{t("home.pricing.description")}</p>
        </div>
        <div className="pricing-grid">
          <article className="price-card">
            <div className="price-plan">{t("home.pricing.demo.name")}<span>01</span></div>
            <p>{t("home.pricing.demo.description")}</p>
            <div className="price-amount"><strong>{t("home.pricing.free")}</strong></div>
            <div className="price-rule" />
            <div className="price-includes">{t("home.pricing.includes")}</div>
            <ul><li><Check size={14} />{t("home.pricing.demo.feature1")}</li><li><Check size={14} />{t("home.pricing.demo.feature2")}</li><li><Check size={14} />{t("home.pricing.demo.feature3")}</li></ul>
            <Link to="/auth/register-demo" className="price-link">{t("home.pricing.demo.cta")}<ArrowUpRight size={15} /></Link>
          </article>
          <article className="price-card">
            <div className="price-plan">{t("home.pricing.single.name")}<span>02</span></div>
            <p>{t("home.pricing.single.description")}</p>
            <div className="price-amount"><strong>{t("home.pricing.single.amount")}</strong><span>{t("home.pricing.month")}</span></div>
            <div className="price-rule" />
            <div className="price-includes">{t("home.pricing.includes")}</div>
            <ul><li><Check size={14} />{t("home.pricing.single.feature1")}</li><li><Check size={14} />{t("home.pricing.single.feature2")}</li><li><Check size={14} />{t("home.pricing.single.feature3")}</li></ul>
            <Link to="/billing?plan=freelance" className="price-link">{t("home.pricing.choosePlan")}<ArrowUpRight size={15} /></Link>
          </article>
          <article className="price-card price-featured">
            <div className="price-badge">{t("home.pricing.popular")}</div>
            <div className="price-plan">{t("home.pricing.team.name")}<span>03</span></div>
            <p>{t("home.pricing.team.description")}</p>
            <div className="price-amount"><strong>{t("home.pricing.team.amount")}</strong><span>{t("home.pricing.month")}</span></div>
            <div className="price-rule" />
            <div className="price-includes">{t("home.pricing.includes")}</div>
            <ul><li><Check size={14} />{t("home.pricing.team.feature1")}</li><li><Check size={14} />{t("home.pricing.team.feature2")}</li><li><Check size={14} />{t("home.pricing.team.feature3")}</li></ul>
            <Link to="/billing?plan=business" className="price-link">{t("home.pricing.choosePlan")}<ArrowUpRight size={15} /></Link>
          </article>
          <article className="price-card price-enterprise">
            <div className="price-plan">{t("home.pricing.enterprise.name")}<span>04</span></div>
            <p>{t("home.pricing.enterprise.description")}</p>
            <div className="price-amount"><strong>{t("home.pricing.enterprise.amount")}</strong><span>{t("home.pricing.month")}</span></div>
            <div className="price-rule" />
            <div className="price-includes">{t("home.pricing.includes")}</div>
            <ul><li><Check size={14} />{t("home.pricing.enterprise.feature1")}</li><li><Check size={14} />{t("home.pricing.enterprise.feature2")}</li><li><Check size={14} />{t("home.pricing.enterprise.feature3")}</li></ul>
            <Link to="/billing?plan=enterprise" className="price-link">{t("home.pricing.choosePlan")}<ArrowUpRight size={15} /></Link>
          </article>
        </div>
        <p className="pricing-disclaimer">{t("home.pricing.disclaimer")}</p>
      </section>

      <section className="home-last-call">
        <div className="last-call-inner">
          <span className="last-call-index">MDRFlow / 05</span>
          <h2>{t("home.last.title")}</h2>
          <p>{t("home.last.body")}</p>
          <Link to="/register" className="home-primary"><span>{t("home.register")}</span><ArrowUpRight size={17} /></Link>
        </div>
      </section>

      <footer className="home-bottom">
        <Link className="home-brand" to="/" aria-label="MDRFlow — European Medical Device Regulatory Platform"><NRSLogo size={30} /><span className="home-wordmark"><NRSWordmark /></span></Link>
        <span>{t("home.footer")}</span>
        <button className="home-cookie-link" type="button" onClick={() => setCookieOpen(true)}>{t("home.cookies.footer")}</button>
        <a href="#top" onClick={(event) => { event.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>{t("home.backTop")}<ArrowUpRight size={13} /></a>
        <small>© 2026 MDRFlow · {t("home.footer.independence")}</small>
      </footer>
    </main>
    <CookiePolicyBanner open={cookieOpen} onDismiss={dismissCookieNotice} t={t} />
    </>
  );
}
